import { normalizeTenantInput } from "./tenantHost";

// Deep links into Admin Studio, e.g. from an external link configured in ISC:
//
//   https://adminstudio.kccs.net/identities/<id>?tenant=company12806-poc
//
// The tenant can be named in the path (/t/company12806-poc/…, the most
// robust form — some link editors drop query strings), explicitly
// (?tenant=…), or as a bare flag-style param (?company12806-poc). Signed out, the page path is remembered for
// the length of the sign-in round trip (sessionStorage — same tab only) and
// restored afterwards; ISC skips its login page when the user already has
// an ISC session, so the link lands on the page with no prompts.

const RETURN_TO_KEY = "adminStudio.returnTo";

function tenantParamKey(params) {
  if (params.get("tenant")) return "tenant";
  for (const [key, value] of params.entries()) {
    if (value === "" && key) return key;
  }
  return null;
}

// /t/<tenant>[/rest] → { tenant, rest } (rest is "/" when absent), else null.
function tenantFromPath(pathname) {
  const m = /^\/t\/([^/]+)(\/.*)?$/.exec(pathname || "");
  if (!m) return null;
  let tenant;
  try { tenant = decodeURIComponent(m[1]); } catch { return null; }
  return { tenant, rest: m[2] || "/" };
}

/** The page path with any /t/<tenant> prefix removed. */
export function pathWithoutTenant(pathname = window.location.pathname) {
  return tenantFromPath(pathname)?.rest || pathname;
}

/** The tenant a deep link names, normalized — or null. */
export function getUrlTenantCandidate(search = window.location.search, pathname = window.location.pathname) {
  const fromPath = tenantFromPath(pathname);
  if (fromPath) return normalizeTenantInput(fromPath.tenant);
  const params = new URLSearchParams(search);
  const key = tenantParamKey(params);
  if (!key) return null;
  return normalizeTenantInput(key === "tenant" ? params.get("tenant") : key);
}

/** The query string with the tenant param taken out ("" or "?…"). */
export function searchWithoutTenant(search = window.location.search) {
  const params = new URLSearchParams(search);
  const key = tenantParamKey(params);
  if (key) params.delete(key);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}

// Only same-app paths: one leading slash (not "//host"), no backslashes or
// scheme tricks, never the OAuth callback itself.
function safePath(path) {
  if (typeof path !== "string" || !path.startsWith("/") || path.startsWith("//")) return null;
  if (/[\\\s]/.test(path) || /^\/auth\/callback\b/.test(path)) return null;
  return path;
}

/** The page the current URL asks for, minus the tenant param — or null for the home page. */
export function currentDeepLinkTarget() {
  const path = `${pathWithoutTenant()}${searchWithoutTenant()}${window.location.hash}`;
  return path === "/" ? null : safePath(path);
}

/** Remember where to go once sign-in completes (or forget, when null). */
export function saveReturnTo(path) {
  try {
    const safe = safePath(path);
    if (safe) sessionStorage.setItem(RETURN_TO_KEY, safe);
    else sessionStorage.removeItem(RETURN_TO_KEY);
  } catch { /* storage unavailable — the link just lands on Home */ }
}

/** The remembered page, cleared as it's read. */
export function takeReturnTo() {
  try {
    const path = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    return safePath(path);
  } catch {
    return null;
  }
}
